import React, { useState } from 'react';
import { getStatusLabel, getStatusColor, formatDate } from '../../utils/helpers';
import { FiCheckCircle, FiCheck, FiX, FiRotateCcw } from 'react-icons/fi';
import { IoBedOutline } from 'react-icons/io5';
import { GiBroom } from 'react-icons/gi';
import clsx from 'clsx';
import ConfirmDialog from '../common/ConfirmDialog';

const CleaningOrderCard = ({ order, onComplete, onUnmark, isCompleting = false, isUnmarking = false }) => {
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showUnmarkConfirm, setShowUnmarkConfirm] = useState(false);
  const isCleaningCompleted = order.cleaning_timestamp !== null;
  const canComplete = !isCleaningCompleted && order.status !== 'cancelled';

  const isCommonSpace = !!order.common_space_id;

  const getLocationLabel = () => {
    if (isCommonSpace) {
      return order.common_space?.name || 'Espacio Común';
    }
    return `Habitación ${order.room?.number || 'N/A'}`;
  };

  const getLocationShortLabel = () => {
    if (isCommonSpace) {
      // Common spaces don't show text in the circle
      return '';
    }
    return order.room?.number || 'N/A';
  };

  const getBedDispositionLabel = () => {
    // Common spaces don't have bed dispositions
    if (isCommonSpace) {
      return 'N/A';
    }
    // First try to get the name from bed_disposition object or bed_disposition_name
    if (order.bed_disposition?.name) {
      return order.bed_disposition.name;
    }
    if (order.bed_disposition_name) {
      return order.bed_disposition_name;
    }
    return 'N/A';
  };

  const getCleaningTypeLabel = () => {
    // First try to get the name from cleaning_type_obj or cleaning_type_name
    if (order.cleaning_type_obj?.name) {
      return order.cleaning_type_obj.name;
    }
    if (order.cleaning_type_name) {
      return order.cleaning_type_name;
    }
    return 'N/A';
  };

  const handleCompleteClick = () => {
    if (canComplete && !isCompleting) {
      setShowCompleteConfirm(true);
    }
  };

  const handleConfirmComplete = () => {
    setShowCompleteConfirm(false);
    onComplete(order.id);
  };

  const handleUnmarkClick = () => {
    if (isCleaningCompleted && !isUnmarking) {
      setShowUnmarkConfirm(true);
    }
  };

  const handleConfirmUnmark = () => {
    setShowUnmarkConfirm(false);
    onUnmark(order.id);
  };

  return (
    <div
      className={clsx(
        'mobile-card transition-all duration-300',
        {
          'bg-green-50 border-green-200': isCleaningCompleted,
          'bg-white border-gray-200': !isCleaningCompleted,
          'opacity-70': isCompleting || isUnmarking,
        }
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <div className={clsx(
            "h-10 w-10 rounded-full flex items-center justify-center",
            {
              'bg-blue-500': !isCommonSpace,
              'bg-green-500': isCommonSpace
            }
          )}>
            <span className="text-base font-bold text-white">
              {getLocationShortLabel()}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">
              {getLocationLabel()}
            </h3>
            {!isCommonSpace && order.room?.floor && (
              <p className="text-xs text-gray-600">
                Piso {order.room.floor}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end">
          <span className={clsx(
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
            getStatusColor(order.status)
          )}>
            {getStatusLabel(order.status)}
          </span>
          {isCleaningCompleted && order.cleaning_timestamp && (
            <span className="text-xs text-gray-500 mt-1">
              {formatDate(new Date(order.cleaning_timestamp), 'HH:mm')}
            </span>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2">
        {/* Bed Configuration, Cleaning Type, and Checkout (only for rooms) - In one row */}
        <div className="bg-gray-50 rounded-lg p-2">
          {isCommonSpace ? (
            // For common spaces: only show cleaning type
            <div className="flex items-center space-x-1 justify-center">
              <GiBroom className="h-5 w-5 text-green-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-gray-500 leading-tight">Tipo de limpieza</p>
                <p className="text-sm font-medium text-gray-900 leading-tight truncate">
                  {getCleaningTypeLabel()}
                </p>
              </div>
            </div>
          ) : (
            // For rooms: show bed disposition, cleaning type, and checkout
            <div className="grid grid-cols-3 gap-1.5">
              <div className="flex items-center space-x-1">
                <IoBedOutline className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 leading-tight">Disposición</p>
                  <p className="text-sm font-medium text-gray-900 leading-tight truncate">
                    {getBedDispositionLabel()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <GiBroom className="h-5 w-5 text-green-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 leading-tight">Tipo</p>
                  <p className="text-sm font-medium text-gray-900 leading-tight truncate">
                    {getCleaningTypeLabel()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <div className={clsx(
                  'flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0',
                  {
                    'bg-green-500': order.checkout_timestamp!==null,
                    'bg-red-500': order.checkout_timestamp===null,
                  }
                )}>
                  {order.checkout_timestamp!==null ? (
                    <FiCheck className="h-3 w-3 text-white" />
                  ) : (
                    <FiX className="h-3 w-3 text-white" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-500 leading-tight">Check-out</p>
                  <p className={clsx(
                    'text-sm font-medium leading-tight',
                    {
                      'text-green-800': order.checkout_timestamp!==null,
                      'text-red-800': order.checkout_timestamp===null,
                    }
                  )}>
                    {order.checkout_timestamp!==null ? 'Sí' : 'No'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Special instructions */}
        {order.notes && (
          <div className="bg-yellow-50 rounded-lg p-2">
            <p className="text-sm text-yellow-800">
              <strong>Instrucciones:</strong> {order.notes}
            </p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {canComplete && (
        <div className="mt-3 pt-2 border-t border-gray-200">
          <button
            onClick={handleCompleteClick}
            disabled={isCompleting}
            className={clsx(
              'w-full py-2 px-4 rounded-md text-sm font-medium transition-colors',
              {
                'bg-green-600 hover:bg-green-700 text-white': !isCompleting,
                'bg-gray-400 text-gray-200 cursor-not-allowed': isCompleting,
              }
            )}
          >
            {isCompleting ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Completando...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <FiCheckCircle className="h-4 w-4" />
                <span>Marcar como Limpia</span>
              </div>
            )}
          </button>
        </div>
      )}

      {isCleaningCompleted && (
        <div className="mt-3 pt-2 border-t border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-green-700">
              <FiCheckCircle className="h-4 w-4" />
              <span className="font-medium text-sm">Limpieza Completada</span>
            </div>
            <button
              onClick={handleUnmarkClick}
              disabled={isUnmarking}
              className={clsx(
                'py-1 px-3 rounded-md text-xs font-medium transition-colors',
                {
                  'bg-yellow-600 hover:bg-yellow-700 text-white': !isUnmarking,
                  'bg-gray-400 text-gray-200 cursor-not-allowed': isUnmarking,
                }
              )}
            >
              {isUnmarking ? (
                <div className="flex items-center space-x-1">
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                  <span>...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1">
                  <FiRotateCcw className="h-3 w-3" />
                  <span>Desmarcar</span>
                </div>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Confirm Complete Dialog */}
      {showCompleteConfirm && (
        <ConfirmDialog
          title="Confirmar limpieza"
          message={`¿Estás seguro de que has terminado la limpieza de ${isCommonSpace ? order.common_space?.name : `la habitación ${order.room?.number}`}?`}
          confirmText="Sí, marcar como limpia"
          confirmStyle="success"
          onConfirm={handleConfirmComplete}
          onCancel={() => setShowCompleteConfirm(false)}
        />
      )}

      {/* Confirm Unmark Dialog */}
      {showUnmarkConfirm && (
        <ConfirmDialog
          title="Desmarcar limpieza"
          message={`¿Estás seguro de que quieres desmarcar como completada ${isCommonSpace ? order.common_space?.name : `la habitación ${order.room?.number}`}?`}
          confirmText="Sí, desmarcar"
          confirmStyle="warning"
          onConfirm={handleConfirmUnmark}
          onCancel={() => setShowUnmarkConfirm(false)}
        />
      )}
    </div>
  );
};

export default CleaningOrderCard;